import { useTranslation } from "react-i18next";
import { Element } from "@/components/validatedShipments/element";
import { ElementPhone } from "@/components/validatedShipments/elementPhone";
import {
  GetAllCompanyDto,
  GetOneExpeditionBoResponseDto,
  PickUpLocationFullCompanyDto,
} from "@/types/api/api-types";

import { SectionTitle } from "./sectionTitle";

type CotationsInfoProps = {
  expedition: GetOneExpeditionBoResponseDto;
  billingEntities: GetAllCompanyDto["billingEntities"];
  pickupLocation: PickUpLocationFullCompanyDto | null;
};

const FALLBACK = "-";

const formatFullName = (first?: string | null, last?: string | null): string => {
  const full = [first, last].filter(Boolean).join(" ").trim();
  return full || FALLBACK;
};

const formatAddress = (parts: Array<string | null | undefined>): string => {
  const cleaned = parts.map((p) => p?.trim()).filter(Boolean);
  return cleaned.length ? cleaned.join(", ") : FALLBACK;
};

export const CotationsInfo = ({
  expedition,
  billingEntities,
  pickupLocation,
}: CotationsInfoProps) => {
  const { t } = useTranslation("boLogistics");
  const { deliveryInfo, pickupInfo, expeditionBillingDetail, createdBy } = expedition;

  const client =
    deliveryInfo?.company || formatFullName(deliveryInfo?.firstName, deliveryInfo?.lastName);

  const billingEntity = billingEntities.find(
    (entity) => entity.id === expeditionBillingDetail?.billingEntityId,
  );

  const deliveryAddress = formatAddress([
    deliveryInfo?.street,
    `${deliveryInfo?.postalCode ?? ""} ${deliveryInfo?.city ?? ""}`,
  ]);

  const pickupAddress = pickupInfo?.pickupLocation
    ? formatAddress([
        pickupInfo.pickupLocation.street,
        `${pickupInfo.pickupLocation.postalCode} ${pickupInfo.pickupLocation.city}`,
      ])
    : formatAddress([
        pickupInfo?.pickupStreet,
        `${pickupInfo?.pickupPostalCode ?? ""} ${pickupInfo?.pickupCity ?? ""}`,
        pickupInfo?.pickupCountry,
      ]);

  const pickupPhone = pickupLocation?.contactNumber ?? pickupInfo?.pickupPhone ?? FALLBACK;

  return (
    <div className="flex flex-col gap-2">
      <SectionTitle>{t("pickupDetailsShipment.title")}</SectionTitle>
      <div className="flex flex-col">
        <Element title={t("pickupDetailsShipment.client")} value={client} />
        <Element
          title={t("pickupDetailsShipment.user")}
          value={formatFullName(createdBy?.firstName, createdBy?.lastName)}
        />
        <Element
          title={t("pickupDetailsShipment.billingEntity")}
          value={billingEntity?.companyName || FALLBACK}
        />
      </div>

      <SectionTitle>{t("pickupDetailsDelivery.title")}</SectionTitle>
      <Element title={t("pickupDetailsDelivery.client")} value={client} />
      <Element title={t("pickupDetailsDelivery.phone")} value={deliveryInfo?.phone || FALLBACK} />
      <Element
        truncate={false}
        title={t("pickupDetailsDelivery.address")}
        value={deliveryAddress}
      />

      <SectionTitle>{t("pickupDetailsPickup.title")}</SectionTitle>
      <Element
        title={t("pickupDetailsPickup.location")}
        value={pickupInfo?.pickupLocation?.name || FALLBACK}
      />
      <ElementPhone title={t("pickupDetailsPickup.phone")} value={pickupPhone} />
      <Element truncate={false} title={t("pickupDetailsPickup.address")} value={pickupAddress} />
      <Element
        truncate={false}
        title={t("pickupDetailsPickup.comment")}
        value={deliveryInfo?.comment || t("pickupDetailsPickup.noComment")}
      />
    </div>
  );
};
